import type React from "react";
import {memo, useCallback} from "react";

import type {LimitDays} from "../../models/pricingRule";
import {convertValuesToOptions} from "../../utils/convertValuesToOptions";
import CheckButton from "../CheckButton";
import {FormControlErrorText} from "../ui/formcontrol";
import {HStack} from "../ui/hstack";

export type LimitDayOfWeek = keyof LimitDays;

export type GroupDays = "allDays" | "weekends" | "weekdays";

export type SelectLimitDaysValue = LimitDayOfWeek | GroupDays;

export type SelectLimitDaysOption<Value = SelectLimitDaysValue> = {
    value: Value;
    label: string;
    order: number;
};

export type GroupType = {
    label: string;
    options: SelectLimitDaysOption[];
};

const daysOfTheWeek: SelectLimitDaysOption<LimitDayOfWeek>[] = [
    {
        value: "monday",
        label: "Monday",
        order: 0
    },
    {
        value: "tuesday",
        label: "Tuesday",
        order: 1
    },
    {
        value: "wednesday",
        label: "Wednesday",
        order: 2
    },
    {
        value: "thursday",
        label: "Thursday",
        order: 3
    },
    {
        value: "friday",
        label: "Friday",
        order: 4
    },
    {
        value: "saturday",
        label: "Saturday",
        order: 5
    },
    {
        value: "sunday",
        label: "Sunday",
        order: 6
    }
];

export type Props = {
    selectedValues?: LimitDays;
    error?: string;
    isDisabled?: boolean;
    onSelectedOption: (option: SelectLimitDaysOption[]) => void;
};

const SelectLimitDays: React.FC<Props> = ({
    error,
    selectedValues,
    isDisabled,
    onSelectedOption
}) => {
    const handleClickDay = useCallback(
        (day: LimitDayOfWeek, isCheck: boolean) => {
            const newValues = {
                ...selectedValues
            };
            if (isCheck) {
                newValues[day] = true;
            } else {
                delete newValues[day];
            }
            const newOptions = convertValuesToOptions(Object.keys(newValues), daysOfTheWeek);
            onSelectedOption(newOptions);
        },
        [onSelectedOption, selectedValues]
    );

    return (
        <>
            <HStack className="flex-wrap" space="sm">
                {daysOfTheWeek.map(option => {
                    const isCheck = !!selectedValues?.[option.value];

                    return (
                        <CheckButton
                            key={option.value}
                            error={!!error}
                            isCheck={isCheck}
                            isDisabled={isDisabled}
                            value={option.value}
                            onClick={handleClickDay}
                        >
                            {option.label}
                        </CheckButton>
                    );
                })}
            </HStack>
            {error && <FormControlErrorText>{error}</FormControlErrorText>}
        </>
    );
};

export default memo(SelectLimitDays);
